// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import { addToEndOfChain, Handler } from "@insite/client-framework/HandlerCreator";
import { getProductById } from "@insite/client-framework/Services/ProductServiceV2";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { AddToWishListParameter, chain } from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { parseProductCategories } from "@sc/XDL/Utilities";

interface Props {
    result: {
        wishList?: WishListModel;
        errorMessage?: string;
    };
}

type HandlerType = Handler<AddToWishListParameter, Props>;

export const _GaSendTracking: HandlerType = async props => {
    const { wishList } = props.result;

    const getProduct = await getProductById({
        id: props.parameter.productInfos?.[0]?.productId,
    });

    const state = props.getState() as xGoogleAnalyticsState;
    if (state.xGoogleAnalytics.addToWishListEvent) {
        const items = props.parameter.productInfos.map(productInfo => ({
            item_name: getProduct?.productTitle,
            item_id: productInfo.productId,
            item_brand: state.xGoogleAnalytics.includeProductBrand ? getProduct?.brand?.name || "" : undefined,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
            price: productInfo.pricing?.unitNetPrice,
            affiliation: state.xGoogleAnalytics.affiliation || "",
            item_list_name: wishList?.name,
            item_list_id: wishList?.id,
            ...parseProductCategories(
                getProduct?.properties?.itemCategoriesShortDescription,
                false,
                state.xGoogleAnalytics.includeProductCategory,
            ),
        }));

        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        const session = getSession(state);

        const googleAnalyticsData = {
            event: state.xGoogleAnalytics.addToWishListEventName,
            ecommerce: {
                value: Number(
                    items
                        .reduce((total, item) => {
                            return total + (item?.price || 0);
                        }, 0)
                        ?.toFixed(2),
                ),
                currency: session.currency?.currencyCode,
                list_name: wishList?.name,
                affiliation: state.xGoogleAnalytics.affiliation || "",
                items,
                sales_rep: state.xGoogleAnalytics.salesRep || "",
                is_rep: state.xGoogleAnalytics.isRep || "",
                bill_to_num: state.xGoogleAnalytics.billToNum || "",
                ship_to_num: state.xGoogleAnalytics.shipToNum || "",
                cust_type: state.xGoogleAnalytics.customerType || "",
                login_type: state.xGoogleAnalytics.loginType || "",
                warehouse: state.xGoogleAnalytics.warehouse || "",
            },
        };

        dataLayer.push({ ecommerce: null });
        dataLayer.push(googleAnalyticsData);
    }
};

addToEndOfChain(chain, _GaSendTracking);
